import { useState, useEffect } from "react";
import { supabase, BRAND, MUTED, FAINT, text, card, btn, btnSm, linkBtn, iconBtn, errorText, savedText, segWrap, segBtn, SHADOW_MODAL, SCRIM, INK, LINE, WHITE, TEXT } from "./constants";
import { JDFormatted, CloseButton } from "./shared";
import { aiFetch } from "./lib/ai";
import { useIsMobile } from "./lib/responsive";
import { logoHtml, LOGO_FONT_LINK } from "./Logo";
import { ChevronDown, X } from "lucide-react";

// Strip the first line of a text if it's a header (markdown ** ** / ## or all-uppercase line).
// Used so the culture doc's own title doesn't duplicate the modal heading.
const stripFirstHeader = (text) => {
  if (!text) return "";
  const lines = text.split("\n");
  const first = (lines[0] || "").trim();
  if (!first) return text;
  const isMd = /^\*\*.+\*\*$/.test(first) || /^#{1,2}\s+.+$/.test(first);
  const isUpper =
    first === first.toUpperCase() &&
    /[A-Za-zÁÉÍÓÚÑáéíóúñ]/.test(first) &&
    first.length > 3 &&
    first.length <= 80 &&
    !/[.?!:,]$/.test(first);
  if (isMd || isUpper) return lines.slice(1).join("\n").replace(/^\n+/, "");
  return text;
};

// Convert a markdown-ish line to an HTML string for PDF rendering.
// Detects: **HEADER** / ## HEADER / uppercase-only line / • or - bullet / inline **bold**.
const lineToHTML = (line) => {
  const t = line.trim();
  if (!t) return '<div style="height:8px"></div>';
  const hm = t.match(/^\*\*(.+?)\*\*$/) || t.match(/^#{1,2}\s+(.+)$/);
  if (hm) return `<div style="font-size:11px;font-weight:800;color:${BRAND};text-transform:uppercase;letter-spacing:1px;margin-top:18px;margin-bottom:5px;">${hm[1]}</div>`;
  const hasLetters = /[A-Za-zÁÉÍÓÚÑáéíóúñ]/.test(t);
  const isAllUpper = t === t.toUpperCase();
  const notTooLong = t.length <= 60;
  const noTrailingPunct = !/[.?!:,]$/.test(t);
  const notBullet = !t.startsWith("•") && !t.startsWith("-");
  if (hasLetters && isAllUpper && notTooLong && noTrailingPunct && notBullet) {
    return `<div style="font-size:11px;font-weight:800;color:${BRAND};text-transform:uppercase;letter-spacing:1px;margin-top:18px;margin-bottom:5px;">${t}</div>`;
  }
  if (t.startsWith("•") || t.startsWith("-")) {
    const txt = t.replace(/^[•-]\s*/, "");
    return `<div style="display:flex;gap:8px;align-items:flex-start;margin-bottom:4px;"><div style="width:4px;height:4px;border-radius:50%;background:${BRAND};margin-top:6px;flex-shrink:0;"></div><span style="font-size:12px;color:${INK};line-height:1.7;">${txt}</span></div>`;
  }
  // Auto-bullets: paragraph with 3+ sentences → render each as a bullet
  const sentences = t.split(". ").filter(s => s.length > 5);
  if (sentences.length >= 3) {
    return sentences.map(s => {
      const sentence = /[.?!]$/.test(s) ? s : s + ".";
      return `<div style="display:flex;gap:8px;align-items:flex-start;margin-bottom:4px;"><div style="width:4px;height:4px;border-radius:50%;background:${BRAND};margin-top:6px;flex-shrink:0;"></div><span style="font-size:12px;color:${INK};line-height:1.7;">${sentence}</span></div>`;
    }).join("");
  }
  const inlineBold = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  return `<div style="font-size:12px;color:${INK};line-height:1.7;margin-bottom:3px;">${inlineBold}</div>`;
};

const DocumentsBox = ({ jobDescription, cultureDoc, positionId, onCultureDocSave, isAdmin = false, companyLogoUrl = null, companyName = "", positionTitle = "", clientCultureDoc = "", lang = "en", onOpenCulture = null }) => {
  const isMobile = useIsMobile();
  const [editing, setEditing] = useState(false);
  const [jdText, setJdText] = useState(jobDescription);
  const [cultureText] = useState(cultureDoc || "");
  const [clientCultureText, setClientCultureText] = useState(clientCultureDoc || "");
  const [qualModalOpen, setQualModalOpen] = useState(false);
  const [qual, setQual] = useState([]);
  const [qualSaving, setQualSaving] = useState(false);
  const [qualSaved, setQualSaved] = useState(false);

  // El cuestionario vive en positions.qual_questions, con secciones e ítems
  useEffect(() => {
    if (!positionId) return;
    supabase.from("positions").select("qual_questions").eq("id", positionId).maybeSingle()
      .then(({ data }) => setQual(data?.qual_questions || []));
  }, [positionId]);


  // Guardar en la posición comprobando que se guardó (23/10): RLS bloquea con
  // 0 filas y sin error, y antes se veía "Guardado" igualmente
  const [saveErr, setSaveErr] = useState("");
  const savePosition = async (fields) => {
    setSaveErr("");
    const { data, error } = await supabase.from("positions").update(fields).eq("id", positionId).select("id");
    const ok = !error && !!data && data.length > 0;
    if (!ok) setSaveErr(lang === "es" ? "No se pudo guardar. Inténtalo de nuevo." : "Couldn't save. Please try again.");
    return ok;
  };
  const saveErrEl = saveErr ? <span style={{ ...errorText, alignSelf: "center" }}>{saveErr}</span> : null;

  const saveQual = async () => {
    setQualSaving(true);
    const ok = await savePosition({ qual_questions: qual });
    setQualSaving(false);
    if (ok) { setQualSaved(true); setTimeout(() => setQualSaved(false), 2000); resetTranslations(); }
  };
  useEffect(() => { if (clientCultureDoc) setClientCultureText(clientCultureDoc); }, [clientCultureDoc]);
  const [editRequested, setEditRequested] = useState(false);
  // Pestaña Documentos: un solo control traduce los tres documentos a la vez.
  // La traducción es de LECTURA: no se guarda, y para editar se vuelve al
  // original. Se recuerda por idioma mientras no cambie el original.
  const [docLang, setDocLang] = useState("original"); // original | es | en
  const [docTr, setDocTr] = useState({});              // { es: { jd, culture, qual }, en: … }
  const [docTrLoading, setDocTrLoading] = useState(null);
  const [docTrError, setDocTrError] = useState("");
  const [openDocs, setOpenDocs] = useState({});      // documentos contraídos o desplegados a mano (sin valor: contraídos en móvil, desplegados en escritorio)
  const resetTranslations = () => { setDocTr({}); setDocLang("original"); setDocTrError(""); };



  // Pide a la IA un texto traducido. Lanza "LIMIT" si el proxy corta por el
  // límite diario, para decirlo en vez de fallar en silencio.
  const aiText = async (content, maxTokens) => {
    const res = await aiFetch({ body: JSON.stringify({ max_tokens: maxTokens, messages: [{ role: "user", content }] }) });
    const data = await res.json().catch(() => ({}));
    if (res.status === 429) throw new Error("LIMIT");
    if (!res.ok) throw new Error(data?.error?.message || data?.error || `HTTP ${res.status}`);
    return (data.content || []).map(b => b.text || "").join("").trim();
  };

  const translateDocs = async (target, cultureSrc) => {
    setDocTrError("");
    if (target === "original" || docTr[target]) { setDocLang(target); return; }
    const langName = target === "es" ? "Spanish" : "English";
    const docPrompt = (text) => `Translate the following document into ${langName}. If it is already in ${langName}, return it unchanged. Preserve the formatting exactly (markdown headers, bullets, numbered lists, bold, line breaks). Return ONLY the translated text, no preamble.\n\n${text}`;
    setDocTrLoading(target);
    try {
      const qualSrc = qual.map(sec => ({ section: sec.section || "", items: (sec.items || []).map(it => ({ n: it.n, q: it.q || "" })) }));
      const [jd, culture, qualRaw] = await Promise.all([
        jdText ? aiText(docPrompt(jdText), 4000) : Promise.resolve(""),
        cultureSrc ? aiText(docPrompt(cultureSrc), 4000) : Promise.resolve(""),
        qualSrc.length ? aiText(`Translate the values of "section" and "q" in this JSON into ${langName}. If they are already in ${langName}, keep them. Keep the structure, the keys and every "n" value exactly as they are. Return ONLY the JSON.\n\n${JSON.stringify(qualSrc)}`, 4000) : Promise.resolve("[]"),
      ]);
      const qualTr = JSON.parse(qualRaw.replace(/```json|```/g, "").trim());
      setDocTr(prev => ({ ...prev, [target]: { jd, culture, qual: Array.isArray(qualTr) ? qualTr : [] } }));
      setDocLang(target);
    } catch (e) {
      console.error("Docs translation error:", e);
      setDocTrError(e.message === "LIMIT"
        ? (lang === "es" ? "Has llegado al límite diario de llamadas a la IA. Vuelve a intentarlo mañana." : "You've reached today's AI limit. Please try again tomorrow.")
        : (lang === "es" ? "No se pudo traducir. Vuelve a intentarlo." : "Couldn't translate. Please try again."));
    }
    setDocTrLoading(null);
  };

  const handleRequestEdit = async () => {
    await supabase.from("admin_notifications").insert({
      type: "jd_edit_request",
      message: `Edit requested for Job Description: ${positionTitle || "position"}`,
      position_title: positionTitle || "",
    });
    setEditRequested(true);
  };

  const handleDownloadPDF = (textOverride) => {
    const pdfText = typeof textOverride === "string" && textOverride ? textOverride : jdText;
    const win = window.open("", "_blank");
    const rutmentLogo = logoHtml(11);
    const clientLogoHtml = companyLogoUrl
      ? `<img src="${companyLogoUrl}" style="height:48px;max-width:160px;object-fit:contain;margin-left:12px;" />`
      : (companyName ? `<span style="font-size:14px;font-weight:700;color:${INK};margin-left:12px;">${companyName}</span>` : "");
    const formattedBody = pdfText.split('\n').map(lineToHTML).join('');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><link href="${LOGO_FONT_LINK}" rel="stylesheet"><title>${positionTitle || (lang === "es" ? "Descripción del puesto" : "Job Description")}</title><style>@page{margin:2cm}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;margin:0;padding:0;color:${INK}}*{box-sizing:border-box}</style></head><body>
      <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:18px;border-bottom:2px solid ${BRAND};margin-bottom:28px;">
        <div>
          <div style="font-size:20px;font-weight:800;color:${INK};letter-spacing:-0.5px;">${positionTitle || (lang === "es" ? "Descripción del puesto" : "Job Description")}</div>
          ${companyName ? `<div style="font-size:13px;color:${MUTED};margin-top:3px;">${companyName}</div>` : ""}
        </div>
        <div style="display:flex;align-items:center;gap:8px;">
          ${clientLogoHtml}
        </div>
      </div>
      <div>${formattedBody}</div>
      <div style="margin-top:48px;padding-top:16px;border-top:1px solid ${LINE};display:flex;align-items:center;gap:6px;">
        <span style="font-size:10px;color:${FAINT};font-weight:500;">${lang === "es" ? "Con la tecnología de" : "Powered by"}</span>
        ${rutmentLogo}
      </div>
    </body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 400);
  };

  // ── Móvil: los modales pasan a hoja a pantalla completa, con cabecera fija
  // (título + cerrar 40×40) y su propio scroll. En escritorio, lo de siempre.
  // Son funciones que devuelven estilos o JSX, no componentes.
  const overlayProps = (onClose) => isMobile
    ? { style: { position: "fixed", inset: 0, background: WHITE, zIndex: 500, display: "flex", flexDirection: "column" } }
    : { style: { position: "fixed", inset: 0, background: SCRIM, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }, onClick: onClose };
  const panelStyle = (maxWidth, maxHeight) => isMobile
    ? { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 16px calc(24px + env(safe-area-inset-bottom))", boxSizing: "border-box" }
    : { background: WHITE, borderRadius: 16, padding: "32px 36px", width: "100%", maxWidth, maxHeight, overflowY: "auto", boxShadow: SHADOW_MODAL };
  const headStyle = (marginBottom) => isMobile
    ? { position: "sticky", top: 0, zIndex: 1, background: WHITE, margin: "0 -16px 16px", padding: "calc(10px + env(safe-area-inset-top)) 16px 10px", borderBottom: `1px solid ${LINE}`, display: "flex", flexDirection: "column", gap: 10 }
    : { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom };
  const sheetTitle = (title, onClose) => isMobile ? (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
      <span style={{ ...text.cardTitle, minWidth: 0 }}>{title}</span>
      <CloseButton onClick={onClose} label={lang === "es" ? "Cerrar" : "Close"} />
    </div>
  ) : <span style={text.cardTitle}>{title}</span>;
  const closeLabel = lang === "es" ? "Cerrar" : "Close";
  // Botones secundarios del modal: fantasma compacto (alto 40 en el móvil)
  const actionsRow = (gap) => ({ display: "flex", gap, ...(isMobile ? { flexWrap: "wrap" } : null) });

  // Cuestionario: editable pregunta a pregunta. Es lo que ve el candidato en
  // el intake, así que cambiarlo aquí cambia el formulario. Solo admin: el
  // cliente ve las preguntas en la tarjeta, pero no las cambia.
  const qualModal = isAdmin && qualModalOpen && (
        <div {...overlayProps(() => setQualModalOpen(false))}>
          <div style={panelStyle(720, "88vh")}
            onClick={e => e.stopPropagation()}>
            <div style={headStyle(8)}>
              {sheetTitle(lang === "es" ? "Cuestionario de cualificación" : "Qualification questionnaire", () => setQualModalOpen(false))}
              <div style={actionsRow(8)}>
                {saveErrEl}
                {qualSaved && <span style={{ ...savedText, alignSelf: "center" }}>{lang === "es" ? "✓ Guardado" : "✓ Saved"}</span>}
                <button onClick={saveQual} disabled={qualSaving}
                  style={{ ...btn.primary, ...btnSm, ...(isMobile ? { height: 40, flex: 1 } : null) }}>
                  {qualSaving ? "..." : (lang === "es" ? "Guardar" : "Save")}
                </button>
                {!isMobile && <CloseButton onClick={() => setQualModalOpen(false)} label={closeLabel} />}
              </div>
            </div>
            <p style={{ fontSize: 12, color: MUTED, margin: "0 0 24px", lineHeight: 1.6 }}>
              {lang === "es" ? "Es lo que responde el candidato al aplicar. Lo que cambies aquí cambia el formulario de intake." : "This is what the candidate answers when applying. Changes here change the intake form."}
            </p>

            {qual.length === 0 && (
              <div style={{ ...text.body, color: MUTED, padding: "24px 0" }}>{lang === "es" ? "Esta posición todavía no tiene cuestionario." : "No questionnaire for this position yet."}</div>
            )}

            {qual.map((sec, si) => (
              <div key={si} style={{ marginBottom: 24 }}>
                <input value={sec.section || ""}
                  onChange={e => setQual(prev => prev.map((x, i2) => i2 === si ? { ...x, section: e.target.value } : x))}
                  style={{ ...text.label, border: "1.5px solid transparent", borderRadius: 8, padding: "4px 8px", marginLeft: -8, marginBottom: 10, width: "100%", outline: "none", fontFamily: "inherit", background: "transparent", ...(isMobile ? { fontSize: 16, minHeight: 44, boxSizing: "border-box", width: "calc(100% + 8px)" } : null) }}
                  onFocus={e => { e.target.style.borderColor = BRAND; e.target.style.background = WHITE; }}
                  onBlur={e => { e.target.style.borderColor = "transparent"; e.target.style.background = "transparent"; }} />

                {(sec.items || []).map((item, ii) => (
                  <div key={ii} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: FAINT, fontWeight: 700, minWidth: 28, paddingTop: 9 }}>{item.n}</span>
                    <textarea value={item.q || ""} rows={2}
                      onChange={e => setQual(prev => prev.map((x, i2) => i2 !== si ? x : {
                        ...x, items: x.items.map((y, j2) => j2 === ii ? { ...y, q: e.target.value } : y),
                      }))}
                      style={{ flex: 1, padding: "8px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, lineHeight: 1.6, outline: "none", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", minWidth: 0 }}
                      onFocus={e => e.target.style.borderColor = BRAND}
                      onBlur={e => e.target.style.borderColor = LINE} />
                    <button onClick={() => setQual(prev => prev.map((x, i2) => i2 !== si ? x : {
                        ...x, items: x.items.filter((_, j2) => j2 !== ii),
                      }))}
                      title={lang === "es" ? "Quitar pregunta" : "Remove question"} aria-label={lang === "es" ? "Quitar pregunta" : "Remove question"}
                      style={{ ...iconBtn, color: FAINT, marginTop: 2, ...(isMobile ? { width: 40, height: 40, marginTop: 0 } : null) }}><X size={16} strokeWidth={1.75} /></button>
                  </div>
                ))}

                <button onClick={() => setQual(prev => prev.map((x, i2) => i2 !== si ? x : {
                    ...x, items: [...(x.items || []), { n: `${si + 1}.${(x.items?.length || 0) + 1}`, q: "" }],
                  }))}
                  style={{ ...linkBtn, marginLeft: 38, ...(isMobile ? { minHeight: 40, fontSize: 13 } : null) }}>
                  {lang === "es" ? "+ Añadir pregunta" : "+ Add question"}
                </button>
              </div>
            ))}

            <button onClick={() => setQual(prev => [...prev, { section: lang === "es" ? "Nueva sección" : "New section", items: [] }])}
              style={{ ...btn.soft, ...btnSm, marginTop: 8, ...(isMobile ? { width: "100%", height: 44, fontSize: 13 } : null) }}>
              {lang === "es" ? "+ Añadir sección" : "+ Add section"}
            </button>
          </div>
        </div>
  );

  // La pestaña Documentos de la posición: los documentos abiertos a página
  // completa. (La antigua caja compacta con modales ya no se usaba: fuera, 24/10.)
  const docCard = { ...card, padding: isMobile ? 16 : 22, minWidth: 0 };
  const cardHead = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, paddingBottom: isMobile ? 12 : 14, marginBottom: isMobile ? 14 : 16, borderBottom: `1px solid ${LINE}`, flexWrap: "wrap" };
  const cardTitle = text.cardTitle;
  const ghost = { ...btn.ghost, ...btnSm, display: "inline-flex", alignItems: "center", gap: 5, ...(isMobile ? { height: 40, padding: "0 14px", fontSize: 13 } : null) };
  const culture = cultureText || clientCultureText;
  const tr = docLang !== "original" ? docTr[docLang] : null;
  const shownJd = tr ? (tr.jd || jdText) : jdText;
  const shownCulture = tr ? (tr.culture || culture) : culture;
  const shownQual = tr && tr.qual.length ? tr.qual : qual;
  const viewingTr = !!tr;
  const backToOriginal = lang === "es" ? "Vuelve al original para editar" : "Switch back to the original to edit";
  // Cada documento se contrae y despliega pulsando su título; contraído no
  // enseña sus botones. Entran contraídos en el móvil y desplegados en el
  // escritorio. Editando el JD se queda abierto.
  const isOpen = (k) => (k === "jd" && editing) || (openDocs[k] ?? !isMobile);
  const head = (k) => isOpen(k) ? cardHead : { ...cardHead, paddingBottom: 0, marginBottom: 0, borderBottom: "none" };
  const toggle = (k, title) => (
    <button onClick={() => setOpenDocs(o => ({ ...o, [k]: !isOpen(k) }))} aria-expanded={isOpen(k)} disabled={k === "jd" && editing}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "none", border: "none", padding: 0, minHeight: isMobile ? 40 : 28, cursor: "pointer", fontFamily: "inherit", textAlign: "left", flex: "1 1 auto", minWidth: 0 }}>
      <ChevronDown size={18} strokeWidth={1.75} color={MUTED} style={{ transform: isOpen(k) ? "none" : "rotate(-90deg)", transition: "transform 0.15s", flexShrink: 0 }} />
      <span style={cardTitle}>{title}</span>
    </button>
  );
  return (
    <>
      {/* Idioma de los documentos: un solo control para los tres */}
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 10, flexWrap: "wrap", marginBottom: 16 }}>
        <span style={text.label}>{lang === "es" ? "Idioma" : "Language"}</span>
        {/* Móvil: el conmutador ocupa todo el ancho, con botones de 40 px */}
        <div style={{ ...segWrap, display: isMobile ? "flex" : "inline-flex", boxSizing: "border-box", alignItems: "center", ...(isMobile ? { width: "100%", overflowX: "auto" } : null) }}>
          {[["original", lang === "es" ? "Original" : "Original"], ["es", "Español"], ["en", "English"]].map(([v, label]) => {
            const active = docLang === v;
            return (
              <button key={v} onClick={() => translateDocs(v, culture)} disabled={!!docTrLoading || editing}
                style={{ ...segBtn(active), cursor: docTrLoading ? "wait" : editing ? "not-allowed" : "pointer", transition: "all 0.12s", ...(isMobile ? { height: 40, padding: "0 14px", flex: "1 0 auto" } : null) }}>
                {docTrLoading === v ? (lang === "es" ? "Traduciendo..." : "Translating...") : label}
              </button>
            );
          })}
        </div>
        {viewingTr && !docTrError && (
          <span style={{ fontSize: 12, color: MUTED }}>{lang === "es" ? "Traducción automática de los tres documentos. No se guarda." : "Automatic translation of all three documents. Not saved."}</span>
        )}
        {docTrError && <span style={errorText}>{docTrError}</span>}
        {saveErrEl}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(auto-fit, minmax(380px, 1fr))", gap: isMobile ? 12 : 16, alignItems: "start" }}>
        {/* Job description */}
        <div style={docCard}>
          <div style={head("jd")}>
            {toggle("jd", lang === "es" ? "Descripción del puesto" : "Job description")}
            {isOpen("jd") && <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button onClick={() => handleDownloadPDF(shownJd)} disabled={!jdText} title={lang === "es" ? "Descargar PDF" : "Download PDF"} style={ghost}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                PDF
              </button>
              {isAdmin ? (
                <button disabled={viewingTr} title={viewingTr ? backToOriginal : ""} onClick={async () => {
                  if (editing) { if (!(await savePosition({ job_description: jdText }))) return; resetTranslations(); }
                  setEditing(v => !v);
                }} style={editing ? { ...ghost, background: BRAND, color: WHITE, borderColor: BRAND, fontWeight: 700 } : viewingTr ? { ...ghost, color: FAINT, cursor: "not-allowed" } : ghost}>
                  {editing ? (lang === "es" ? "Guardar" : "Save") : (lang === "es" ? "Editar" : "Edit")}
                </button>
              ) : editRequested ? (
                <span style={savedText}>{lang === "es" ? "✓ Pedido" : "✓ Requested"}</span>
              ) : (
                <button onClick={handleRequestEdit} style={ghost}>
                  {lang === "es" ? "Pedir cambio" : "Request edit"}
                </button>
              )}
            </div>}
          </div>
          {!isOpen("jd") ? null : editing ? (
            <textarea value={jdText} onChange={e => setJdText(e.target.value)} rows={isMobile ? 16 : 24}
              style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", border: `1.5px solid ${BRAND}`, borderRadius: 10, fontSize: isMobile ? 16 : 13, lineHeight: 1.75, resize: "vertical", outline: "none", fontFamily: "inherit", color: INK }} />
          ) : jdText ? (
            <JDFormatted text={shownJd} />
          ) : (
            <div style={{ ...text.body, color: MUTED }}>{lang === "es" ? "Esta posición todavía no tiene job description." : "This position has no job description yet."}</div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 12 : 16, minWidth: 0 }}>
          {/* Cuestionario de cualificación */}
          <div style={docCard}>
            <div style={head("qual")}>
              {toggle("qual", lang === "es" ? "Cuestionario de cualificación" : "Qualification questionnaire")}
              {isAdmin && isOpen("qual") && <button onClick={() => setQualModalOpen(true)} disabled={viewingTr} title={viewingTr ? backToOriginal : ""} style={viewingTr ? { ...ghost, color: FAINT, cursor: "not-allowed" } : ghost}>{lang === "es" ? "Editar" : "Edit"}</button>}
            </div>
            {isOpen("qual") && <>
            <p style={{ fontSize: 12, color: MUTED, margin: "0 0 14px", lineHeight: 1.6 }}>
              {lang === "es" ? "Lo que responde el candidato al aplicar." : "What the candidate answers when applying."}
            </p>
            {qual.length === 0 ? (
              <div style={{ ...text.body, color: MUTED }}>{lang === "es" ? "Esta posición todavía no tiene cuestionario." : "No questionnaire for this position yet."}</div>
            ) : shownQual.map((sec, si) => (
              <div key={si} style={{ marginBottom: 16 }}>
                <div style={{ ...text.label, marginBottom: 6 }}>{sec.section}</div>
                {(sec.items || []).map((item, ii) => (
                  <div key={ii} style={{ display: "flex", gap: 10, fontSize: 13, color: TEXT, lineHeight: 1.6, marginBottom: 4 }}>
                    <span style={{ color: FAINT, fontWeight: 700, minWidth: 26 }}>{item.n}</span>
                    <span>{item.q}</span>
                  </div>
                ))}
              </div>
            ))}
            </>}
          </div>

          {/* Cultura: la propia de la posición, si la tiene. La del cliente ya
              vive en su página (Culture statement): aquí solo un enlace, para
              no tener el mismo documento en dos sitios (31/10) */}
          {!cultureText && clientCultureText && onOpenCulture && (
            <button type="button" onClick={onOpenCulture} style={{ ...docCard, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, width: "100%", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
              <span style={text.cardTitle}>{lang === "es" ? "Cultura de la empresa" : "Company culture"}</span>
              <span style={{ ...linkBtn }}>{lang === "es" ? "Ver Culture statement →" : "Open Culture statement →"}</span>
            </button>
          )}
          {(cultureText || (clientCultureText && !onOpenCulture)) && (
            <div style={docCard}>
              <div style={head("culture")}>
                {toggle("culture", lang === "es" ? "Cultura de la empresa" : "Company culture")}
              </div>
              {isOpen("culture") && <JDFormatted text={stripFirstHeader(shownCulture)} />}
            </div>
          )}
        </div>
      </div>
      {qualModal}
    </>
  );
};
// ─── CANDIDATES VIEW ──────────────────────────────────────────────────────────

export default DocumentsBox;
